import { RefObject, useRef } from "react";
import { useReactiveVar } from "@apollo/client";
import { IconButton } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { BundleBlockTitle } from "./style";
import {
  CurrentBundleContainer as BundleContainer,
  MobileAppModalBundleName as BundleTitle,
} from "../../../../../MobileAppModal/MobileAppModal.style";
import {
  ButtonCustom,
  ImportFileUpload,
  BundleLink,
} from "../../../../../shared/Style/Style";
import { robotsState } from "../../../state";
import { GameModelArtifactFileTypeEnum } from "../../../../../../generated/graphql";
import {
  BundleMode,
  BundleNames,
} from "../../../../../MobileAppModal/MobileAppModelModal/constants";
import { ArtifactBundleTypesMap } from "../../../maps";

export const ArtifactBundles = () => {
  const iosInput = useRef<HTMLInputElement | null>(null);
  const iosOptimizedInput = useRef<HTMLInputElement | null>(null);
  const androidInput = useRef<HTMLInputElement | null>(null);
  const androidOptimizedInput = useRef<HTMLInputElement | null>(null);
  const webGLInput = useRef<HTMLInputElement | null>(null);
  const webGLOptimizedInput = useRef<HTMLInputElement | null>(null);
  const manifestInputIos = useRef<HTMLInputElement | null>(null);
  const manifestOptimizedInputIos = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const manifestOptimizedInputAndroid = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGL = useRef<HTMLInputElement | null>(null);
  const manifestOptimizedInputWebGL = useRef<HTMLInputElement | null>(null);

  const artifact = useReactiveVar(robotsState.artifact);
  const isLoading = useReactiveVar(robotsState.isLoading);

  const uploadArtifactBundle = async (
    file: File,
    fileType: GameModelArtifactFileTypeEnum
  ) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      robotsState.updateArtifactFile(
        {
          updateGameModelArtifactFileId: artifact?.id,
          file,
          fileType,
        },
        () => {
          robotsState.setArtifactFile(fileType, "#");
        }
      );
    };
    reader.readAsDataURL(file);
  };

  const deleteArtifactBundle = async (
    fileType: GameModelArtifactFileTypeEnum
  ) => {
    robotsState.deleteArtifactFile(
      {
        deleteGameModelArtifactFileId: artifact?.id,
        fileType,
      },
      () => {
        robotsState.setArtifactFile(fileType, "");
      }
    );
  };

  const bundlesData = [
    {
      name: BundleNames.Ios,
      currentBundle: artifact?.iosBundle,
      type: BundleMode.Bundle,
      inputRef: iosInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(file, GameModelArtifactFileTypeEnum.IosBundle),
    },
    {
      name: BundleNames.IosOptimized,
      currentBundle: artifact?.iosBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: iosOptimizedInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.IosBundleOptimized
        ),
    },
    {
      name: BundleNames.Android,
      currentBundle: artifact?.androidBundle,
      type: BundleMode.Bundle,
      inputRef: androidInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(file, GameModelArtifactFileTypeEnum.AndroidBundle),
    },
    {
      name: BundleNames.AndroidOptimized,
      currentBundle: artifact?.androidBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: androidOptimizedInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.AndroidBundleOptimized
        ),
    },
    {
      name: BundleNames.WebGlBundleClothesObjects,
      currentBundle: artifact?.webGLBundle,
      type: BundleMode.Bundle,
      inputRef: webGLInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(file, GameModelArtifactFileTypeEnum.WebGlBundle),
    },
    {
      name: BundleNames.WebGlOptimizedBundleClothesObjects,
      currentBundle: artifact?.webGLBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: webGLOptimizedInput,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.WebGlBundleOptimized
        ),
    },
    {
      name: BundleNames.IosManifest,
      currentBundle: artifact?.iosManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputIos,
      handleBundle: (file: File) =>
        uploadArtifactBundle(file, GameModelArtifactFileTypeEnum.IosManifest),
    },
    {
      name: BundleNames.IosOptimizedManifest,
      currentBundle: artifact?.iosManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestOptimizedInputIos,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.IosManifestOptimized
        ),
    },
    {
      name: BundleNames.AndroidManifest,
      currentBundle: artifact?.androidManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputAndroid,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.AndroidManifest
        ),
    },
    {
      name: BundleNames.AndroidOptimizedManifest,
      currentBundle: artifact?.androidManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestOptimizedInputAndroid,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.AndroidManifestOptimized
        ),
    },
    {
      name: BundleNames.WebGlManifestClothesObjects,
      currentBundle: artifact?.webGLManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputWebGL,
      handleBundle: (file: File) =>
        uploadArtifactBundle(file, GameModelArtifactFileTypeEnum.WebGlManifest),
    },
    {
      name: BundleNames.WebGlOptimizedManifestClothesObjects,
      currentBundle: artifact?.webGLManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestOptimizedInputWebGL,
      handleBundle: (file: File) =>
        uploadArtifactBundle(
          file,
          GameModelArtifactFileTypeEnum.WebGlManifestOptimized
        ),
    },
  ];

  const renderCurrentBundle = (
    name: BundleNames,
    currentBundle: string,
    type: BundleMode,
    inputRef: RefObject<HTMLInputElement>,
    handleBundle: (file: File) => void
  ) => {
    const label = type === BundleMode.Manifest ? `${name}` : `${name} ${type}`;
    const fileType = ArtifactBundleTypesMap.get(name);

    return (
      <BundleContainer key={name}>
        <BundleTitle>{name}</BundleTitle>
        {!!currentBundle ? (
          <span>
            <BundleLink href={currentBundle}>{label}</BundleLink>
            <IconButton
              disabled={isLoading}
              onClick={() => deleteArtifactBundle(fileType)}
            >
              <ClearIcon color="error" fontSize="small" />
            </IconButton>
          </span>
        ) : (
          <ButtonCustom
            disabled={isLoading}
            onClick={() => inputRef.current.click()}
          >
            <span>Прикріпити</span>
            <ImportFileUpload
              type="file"
              ref={inputRef}
              onChange={({ target: { files } }) =>
                files[0] && handleBundle(files[0])
              }
            />
          </ButtonCustom>
        )}
      </BundleContainer>
    );
  };

  return (
    <>
      <BundleBlockTitle>Bundle, manifest</BundleBlockTitle>
      {bundlesData.map(
        ({ name, currentBundle, type, inputRef, handleBundle }) =>
          renderCurrentBundle(name, currentBundle, type, inputRef, handleBundle)
      )}
    </>
  );
};
